<template>
  <div>
    <!-- 검색 -->
    <vx-card class="search-nav mb-4">
      <div class="flex flex-wrap gap-4">
        <div class="w-full xl:w-1/4" v-for="field in searchFields" :key="field.key">
          <search-label-body :label="field.label" columns="80px 1fr">
            <vs-select class="w-full" v-model="nav[field.key]" autocomplete>
              <vs-select-item value="" text="전체" />
              <vs-select-item v-for="v in navOptions[field.key]" :key="`${field.key}-${v}`" :value="v" :text="v" />
            </vs-select>
          </search-label-body>
        </div>
      </div>
      <div class="flex justify-end mt-4">
        <vs-button color="secondary" @click="onClickSearch">검색</vs-button>
      </div>
    </vx-card>

    <!-- 목록 -->
    <vx-card>
      <div class="flex justify-between items-center mb-4">
        <vs-select class="usage-perpage-select" v-model="perPage" autocomplete>
          <vs-select-item value="전체보기" text="전체보기" />
          <vs-select-item v-for="v in [10, 20, 30, 50]" :key="`per-${v}`" :text="`${v}개씩 보기`" :value="v" />
        </vs-select>
        <vs-button color="secondary" type="border" @click="onClickExcelDownload">List Excel 다운로드</vs-button>
      </div>

      <div class="w-full overflow-x-auto">
        <table class="usage-table">
          <thead>
            <tr>
              <th v-for="head in heads" :key="head.key" @click="onClickSort(head.key)">
                <span class="th-inner">
                  <span>{{ head.name }}</span>
                  <span class="sort-icon">
                    <i class="arrow-up" :class="{ active: sort.key === head.key && sort.order === 'asc' }" />
                    <i class="arrow-down" :class="{ active: sort.key === head.key && sort.order === 'desc' }" />
                  </span>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="4">
                <div class="w-full p-5 flex items-center justify-center" style="min-height: 100px">
                  <feather-icon icon="LoaderIcon" class="mr-2 rotate-icon" />
                  데이터를 불러오는 중입니다.
                </div>
              </td>
            </tr>
            <tr v-else-if="sortedList.length === 0">
              <td colspan="4">
                <div class="w-full p-5 flex items-center justify-center" style="min-height: 100px">
                  <feather-icon icon="AlertTriangleIcon" class="mr-2" />
                  표시할 데이터가 없습니다.
                </div>
              </td>
            </tr>
            <tr v-else v-for="(row, index) in pagedList" :key="index">
              <td>{{ row.사업장명 }}</td>
              <td>{{ row.공장명 }}</td>
              <td>{{ row.공정명 }}</td>
              <td>{{ row.사용제품 }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="vs-con-table" v-if="perPage !== '전체보기' && sortedList.length > 0">
        <div class="con-pagination-table vs-table--pagination">
          <k-pagination :total="lastPage" v-model="page"></k-pagination>
        </div>
      </div>
    </vx-card>

    <popup-excel-download v-model="openExcelDownload" :list="excelList" title="제품 사용 현황" />
  </div>
</template>

<script>
import SearchLabelBody from "@/views/list/components/base/SearchLabelBody.vue";
import PopupExcelDownload from "@/popup/PopupExcelDownload.vue";
import KPagination from "@/components/kPagination/kPagination.vue";
import * as DataUtil from "@/util/DataUtil";
import { getHandlingDepts } from "./handlingDeptCompat";

export default {
  name: "Page",
  components: {
    SearchLabelBody,
    PopupExcelDownload,
    KPagination,
  },
  data() {
    return {
      loading: false,
      rawList: [],
      nav: {
        사업장명: "",
        공장명: "",
        공정명: "",
        사용제품: "",
      },
      appliedNav: {
        사업장명: "",
        공장명: "",
        공정명: "",
        사용제품: "",
      },
      perPage: "전체보기",
      page: 1,
      sort: {
        key: "",
        order: "",
      },
      searchFields: [
        { label: "사업장명", key: "사업장명" },
        { label: "공장명", key: "공장명" },
        { label: "공정명", key: "공정명" },
        { label: "사용 제품", key: "사용제품" },
      ],
      heads: [
        { name: "사업장명", key: "사업장명" },
        { name: "공장명", key: "공장명" },
        { name: "공정명", key: "공정명" },
        { name: "사용 제품", key: "사용제품" },
      ],
      openExcelDownload: false,
    };
  },
  computed: {
    projectId() {
      return this.$route.params.proj_id;
    },
    // 검색 셀렉트 선택지: 등록된 데이터의 값 중 앞 칸(사업장명 → 공장명 → 공정명 → 사용제품)에서 고른 값에 해당하는 것만
    navOptions() {
      const options = {};
      this.searchFields.forEach((field, index) => {
        const upperKeys = this.searchFields.slice(0, index).map((f) => f.key);
        const set = new Set();
        for (const row of this.rawList) {
          if (!row[field.key]) continue;
          if (upperKeys.some((key) => this.nav[key] && row[key] !== this.nav[key])) continue;
          set.add(row[field.key]);
        }
        options[field.key] = [...set].sort();
      });
      return options;
    },
    filteredList() {
      return this.rawList.filter((row) => this.searchFields.every((field) => !this.appliedNav[field.key] || row[field.key] === this.appliedNav[field.key]));
    },
    sortedList() {
      const list = [...this.filteredList];
      if (this.sort.key && this.sort.order) {
        list.sort((a, b) => {
          const valueA = a[this.sort.key] || "";
          const valueB = b[this.sort.key] || "";
          const compare = valueA > valueB ? 1 : valueA < valueB ? -1 : 0;
          return this.sort.order === "asc" ? compare : -compare;
        });
      }
      return list;
    },
    lastPage() {
      if (this.perPage === "전체보기") return 1;
      return Math.max(1, Math.ceil(this.sortedList.length / this.perPage));
    },
    pagedList() {
      if (this.perPage === "전체보기") return this.sortedList;
      const start = (this.page - 1) * this.perPage;
      return this.sortedList.slice(start, start + this.perPage);
    },
    excelList() {
      return this.sortedList.map((row, index) => ({
        "No": index + 1,
        "사업장명": row.사업장명,
        "공장명": row.공장명,
        "공정명": row.공정명,
        "사용 제품": row.사용제품,
      }));
    },
  },
  watch: {
    perPage() {
      this.page = 1;
    },
    appliedNav() {
      this.page = 1;
    },
    // 앞 칸을 바꿔서 뒤 칸에 고른 값이 선택지에서 빠지면 비운다
    navOptions(options) {
      for (const field of this.searchFields) {
        if (this.nav[field.key] && !options[field.key].includes(this.nav[field.key])) this.nav[field.key] = "";
      }
    },
    sort: {
      deep: true,
      handler() {
        this.page = 1;
      },
    },
  },
  created() {
    this.loadList();
  },
  methods: {
    async loadList() {
      this.loading = true;
      try {
        const posts = await DataUtil.loadDefragmentation(this.$store, "board/LOAD_POST_LIST", {
          projectId: this.projectId,
          boardId: 1030,
          page: 1,
          perPage: 1000,
          orders: [{ target: "$created_at", direction: "desc" }],
        });

        const rows = [];
        for (const post of posts) {
          if (!post.post_content) continue;

          let body = null;
          try {
            body = JSON.parse(post.post_content).body;
          } catch (e) {
            continue;
          }

          const 제품명 = (body && body.제품명) || "";
          const 취급부서 = getHandlingDepts(body); // 개선안 이전 문서의 공장명/공정명도 한 행으로 포함
          for (const dept of 취급부서) {
            if (!dept.사업장 && !dept.공장명 && !dept.공정명) continue;
            rows.push({
              사업장명: dept.사업장 || "",
              공장명: dept.공장명 || "",
              공정명: dept.공정명 || "",
              사용제품: 제품명,
            });
          }
        }

        this.rawList = rows;
      } catch (e) {
        console.error("loadList err", e);
      } finally {
        this.loading = false;
      }
    },
    onClickSearch() {
      this.appliedNav = { ...this.nav };
    },
    onClickSort(key) {
      if (this.sort.key !== key) {
        this.sort = { key, order: "asc" };
      } else if (this.sort.order === "asc") {
        this.sort = { key, order: "desc" };
      } else {
        this.sort = { key: "", order: "" };
      }
    },
    onClickExcelDownload() {
      this.openExcelDownload = true;
    },
  },
};
</script>

<style scoped lang="scss">
.usage-perpage-select {
  width: 160px;
}

.rotate-icon {
  animation: rotate 2s linear infinite;
}

table.usage-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;

  th,
  td {
    border: 1px solid #cdcdcd;
    text-align: center;
  }

  th {
    background: #fafafa;
    font-size: 14px;
    font-weight: 500;
    padding: 0;
    cursor: pointer;
    user-select: none;

    .th-inner {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      padding: 10px 0;
    }
  }

  td {
    font-size: 14px;
    padding: 11px 0;
  }

  .sort-icon {
    display: inline-flex;
    flex-direction: column;
    gap: 2px;

    i {
      width: 0;
      height: 0;
      border-left: 4px solid transparent;
      border-right: 4px solid transparent;
    }

    i.arrow-up {
      border-bottom: 5px solid #d2d2d2;

      &.active {
        border-bottom-color: #6670cb;
      }
    }

    i.arrow-down {
      border-top: 5px solid #d2d2d2;

      &.active {
        border-top-color: #6670cb;
      }
    }
  }
}
</style>
